'use client';

import Link from 'next/link';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { classifyLink } from '@/lib/assistant/links';

/**
 * One assistant message.
 *
 * Raw HTML stays off: `react-markdown` ignores it unless `rehype-raw` is added,
 * and it is deliberately not added. Images are stripped for the same reason a
 * javascript: href is — a remote image in model output shaped by tool results
 * is an unreviewed request to a third party.
 *
 * `data-testid` lets `BlockList` be asserted on structurally (does a message
 * bubble exist at all) rather than only by its rendered text, which matters
 * for proving an empty-text turn renders nothing here.
 */
export function MarkdownBlock({ text, onNavigate }: { text: string; onNavigate?: () => void }) {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;

  return (
    <div
      data-testid="assistant-text-block"
      className="prose prose-sm max-w-none text-sm leading-relaxed dark:prose-invert [&_p]:my-2 [&_ul]:my-2"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        disallowedElements={['img']}
        unwrapDisallowed
        components={{
          a({ href, children }) {
            const target = classifyLink(String(href ?? ''), origin);

            if (target.kind === 'unsafe') return <>{children}</>;

            if (target.kind === 'internal') {
              return (
                <Link href={target.path} onClick={onNavigate} className="underline underline-offset-2">
                  {children}
                </Link>
              );
            }
            return (
              <a
                href={target.href}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2"
              >
                {children}
              </a>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
